import { Fragment, h, Host } from "@stencil/core";
import { getAuthClient } from "@trumpf/ux.utils";
import { debounce } from "../../utils/debounce";
import { extractJwtPayload, getCheckSessionIframeSrcdoc, getCodeChallenge, getCodeVerifier, getQueryString, getRandomString, getUrlOrigin } from "./silent-auth-utils";
const TRUMPF_ISSUERS_PROD = ["https://account.trumpf.com"];
const TRUMPF_ISSUERS_STAGE = ["https://account-s.trumpf.com", "https://account-t.trumpf.com"];
const TRUMPF_CALLBACK_URI_PROD = "https://cdn.trumpf.com/static/auth-callback.html";
const TRUMPF_CALLBACK_URI_STAGE = "https://cdn-q.trumpf.com/static/auth-callback.html";
const TRUMPF_CHECK_SESSION_URI_PROD = "https://cdn.trumpf.com/static/auth-checksession.html";
const TRUMPF_CHECK_SESSION_URI_STAGE = "https://cdn-q.trumpf.com/static/auth-checksession.html";
export class SilentAuth {
    constructor() {
        this.authTokenData = null;
        this.authIframe = null;
        this.wasLoggedIn = false;
        this.openIdConfiguration = {};
        this.activeSessionIframe = null;
        this._log = (msg) => {
            /* eslint-disable no-console */
            if (this.debug && typeof console !== "undefined" && typeof console.log === "function") {
                const n = this.clientName.length > 0 ? `SilentAuth (${this.clientName})` : "SilentAuth";
                console.log(`[${n}]: ${msg}`);
            }
            /* eslint-enable no-console */
        };
        this.loadOpenIdConfiguration = async () => {
            const res = await fetch(`${this.issuer}/.well-known/openid-configuration`, {});
            if (res.ok) {
                return await res.json();
            }
            return {};
        };
        this.getHasActiveSession = async () => {
            return new Promise((resolve) => {
                if (!this.activeSessionIframe) {
                    this._log("No active session iframe");
                    resolve(undefined);
                    return;
                }
                let hasActiveSession = undefined;
                const interval = window.setInterval(() => {
                    var _a;
                    if ((_a = this.activeSessionIframe) === null || _a === void 0 ? void 0 : _a.contentWindow) {
                        this.activeSessionIframe.contentWindow.postMessage(this.clientId, getUrlOrigin(this.issuer));
                    }
                }, 250);
                const timeout = window.setTimeout(() => {
                    window.clearInterval(interval);
                    window.removeEventListener("message", activeSessionMessageHandler, true);
                    resolve(hasActiveSession);
                }, 3000);
                const activeSessionMessageHandler = (event) => {
                    if (event.origin === getUrlOrigin(this.issuer) && typeof event.data === "boolean") {
                        window.clearInterval(interval);
                        window.clearTimeout(timeout);
                        window.removeEventListener("message", activeSessionMessageHandler, true);
                        hasActiveSession = event.data;
                        resolve(hasActiveSession);
                    }
                };
                window.addEventListener("message", activeSessionMessageHandler, true);
            });
        };
        this.startActiveSessionListener = () => {
            const check = async () => {
                const hasActiveSession = await this.getHasActiveSession();
                if (hasActiveSession === true) {
                    this._log("User logged in");
                    this.stopActiveSessionListener();
                    this.initSilentAuth();
                }
                else if (hasActiveSession === false) {
                    this.activeSessionTimeout = window.setTimeout(check, 2000);
                }
            };
            this.activeSessionTimeout = window.setTimeout(check, 2000);
        };
        this.stopActiveSessionListener = () => {
            window.clearTimeout(this.activeSessionTimeout);
        };
        this.initSilentAuth = async () => {
            this.stopCheckSession();
            this.cancelKeepAliveTimeout();
            if (!this.authIframe) {
                return;
            }
            if (!this.openIdConfiguration.authorization_endpoint) {
                this._log("Loading openid-configuration");
                this.openIdConfiguration = await this.loadOpenIdConfiguration();
            }
            const hasActiveSession = await this.getHasActiveSession();
            if (hasActiveSession === true) {
                this._log("User has active session");
            }
            else if (hasActiveSession === false) {
                this._log("No active session");
                this.loggedIn = false;
                if (this.wasLoggedIn) {
                    this.uxLogout.emit({ client_name: this.clientName });
                    this.wasLoggedIn = false;
                }
                this.uxAuth.emit({ success: false, error: "login_required", client_name: this.clientName });
                this.startActiveSessionListener();
                return;
            }
            this.stopActiveSessionListener();
            this._log("Init silent auth");
            this.loggedIn = undefined;
            const payload = await this.initAuthPayload();
            this.initAuthCallback(this.authIframe);
            this.authIframe.src = `${this.openIdConfiguration.authorization_endpoint}?${getQueryString(payload)}`;
            // Timeout if the auth iframe did not receive a message within failedTimeout.
            window.clearTimeout(this.authFailureTimeout);
            this.authFailureTimeout = window.setTimeout(() => {
                if (this.loggedIn === undefined) {
                    if (this.wasLoggedIn) {
                        this.uxLogout.emit({ client_name: this.clientName });
                        this.wasLoggedIn = false;
                    }
                    this.uxAuth.emit({ success: false, error: "timeout", client_name: this.clientName });
                }
            }, this.failedTimeout * 1000);
        };
        this.initSilentAuthDebounced = debounce(this.initSilentAuth, 100);
        this.initAuthCallback = (iframe) => {
            if (typeof this.authCallbackHandler === "function") {
                window.removeEventListener("message", this.authCallbackHandler, true);
            }
            const authMessageHandler = (event) => {
                if (event.origin !== getUrlOrigin(this.redirectUri)) {
                    return;
                }
                if (event.source !== iframe.contentWindow) {
                    return;
                }
                window.removeEventListener("message", this.authCallbackHandler, true);
                const { code, error } = event.data;
                if (typeof code === "string") {
                    this._log("Got one-time code");
                    this.requestToken(code);
                    return;
                }
                if (typeof error === "string") {
                    this._log(`Error: ${error}`);
                    this.authClient.deleteAccessToken();
                    this.authTokenData = null;
                    this.loggedIn = false;
                    if (this.wasLoggedIn) {
                        this.uxLogout.emit({ client_name: this.clientName });
                        this.wasLoggedIn = false;
                    }
                    this.uxAuth.emit({ success: false, error: error, client_name: this.clientName });
                    return;
                }
            };
            this.authCallbackHandler = authMessageHandler.bind(this);
            window.addEventListener("message", this.authCallbackHandler, true);
        };
        this.collectScopes = () => {
            return this.scope.split(" ").sort().join(" ");
        };
        this.initAuthPayload = async () => {
            var _a;
            this.codeVerifier = getCodeVerifier();
            const codeChallenge = await getCodeChallenge(this.codeVerifier);
            return {
                client_id: this.authClientId,
                code_challenge: codeChallenge,
                code_challenge_method: "S256",
                prompt: "none",
                redirect_uri: this.redirectUri,
                response_type: "code",
                scope: this.collectScopes(),
                state: ((_a = window.top) === null || _a === void 0 ? void 0 : _a.location.href) || window.location.href
            };
        };
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        this.requestToken = async (code) => {
            this._log("Request token with authorization_code grant");
            return this.sendTokenRequest({
                grant_type: "authorization_code",
                response_type: "token",
                redirect_uri: this.redirectUri,
                client_id: this.authClientId,
                code_verifier: this.codeVerifier,
                code
            });
        };
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        this.sendTokenRequest = (data) => {
            return fetch(this.openIdConfiguration.token_endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                redirect: "follow",
                body: getQueryString(data)
            })
                .then((response) => {
                if (response.ok) {
                    return response.json();
                }
                return Promise.reject(new Error("Failed to fetch token"));
            })
                .then((data) => {
                this._log("Got access token");
                this.handleTokenResponse(data);
                return data;
            })
                .catch((error) => console.log(error));
        };
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        this.handleTokenResponse = (data) => {
            if (typeof data === "object" && typeof data.access_token === "string") {
                this.authTokenData = data;
                let keepAliveDelay = this.keepAliveTimeout;
                if (this.authTokenData.expires_in > 0 && this.authTokenData.expires_in < keepAliveDelay) {
                    keepAliveDelay = this.authTokenData.expires_in;
                }
                try {
                    const payload = extractJwtPayload(this.authTokenData.id_token);
                    if (payload.custom_attributes && payload.custom_attributes.sessionState) {
                        this.sessionState = payload.custom_attributes.sessionState;
                        this._log("Got session state");
                    }
                    else {
                        this._log("No session state found in id_token");
                    }
                }
                catch (_a) {
                    this._log("Failed to extract session state from id_token");
                }
                this.loggedIn = true;
                this.wasLoggedIn = true;
                // Distribute token data to auth client
                this.authClient.setTokenData(this.authTokenData);
                // Emit uxAuth event to inform other components about the new token data
                this.uxAuth.emit(Object.assign({ success: true, error: null, client_name: this.clientName }, data));
                this._log(`Starting keep alive timeout (${keepAliveDelay} seconds)`);
                this.keepAliveTimeoutId = window.setTimeout(this.keepAlive, keepAliveDelay * 1000);
            }
            else {
                this.authTokenData = null;
                this.loggedIn = false;
                this.uxAuth.emit({ success: false, client_name: this.clientName, error: "invalid_token_response" });
            }
        };
        this.cancelKeepAliveTimeout = () => {
            window.clearTimeout(this.keepAliveTimeoutId);
        };
        this.keepAlive = () => {
            this._log(`Keep alive ${new Date()}`);
            this.initSilentAuth();
        };
        this.startCheckSession = () => {
            this.stopCheckSession();
            if (!this.loggedIn || !this.checkSessionEnabled) {
                return;
            }
            this._log("Starting check-session");
            this.checkSessionActive = true;
            this.didReceiveFirstCheckSessionMessage = false;
            window.addEventListener("message", this.handleCheckSessionMessage, true);
        };
        this.stopCheckSession = () => {
            if (this.checkSessionActive) {
                this._log("Stopping check-session");
                window.removeEventListener("message", this.handleCheckSessionMessage, true);
                this.checkSessionActive = false;
                this.didReceiveFirstCheckSessionMessage = false;
            }
        };
        this.didReceiveFirstCheckSessionMessage = false;
        this.handleCheckSessionMessage = (event) => {
            if (event.origin !== window.origin && event.origin !== getUrlOrigin(this.redirectUri)) {
                return;
            }
            if (!event.data || typeof event.data !== "string") {
                return;
            }
            const messageParts = event.data.split(" ");
            const messageBody = messageParts[0];
            // Only handle valid message types
            if (!["unchanged", "changed", "error"].includes(messageBody)) {
                return;
            }
            // Only handle message with matching session
            if (messageParts[1] !== this.sessionState) {
                return;
            }
            if (!this.didReceiveFirstCheckSessionMessage) {
                this._log(`Received 1st check-session message: "${messageBody}"`);
            }
            this.didReceiveFirstCheckSessionMessage = true;
            switch (messageBody) {
                case "unchanged":
                    break;
                case "changed":
                    this._log("Session changed");
                    this.stopCheckSession();
                    this.initSilentAuth();
                    break;
                case "error":
                    this._log("Check session error");
                    this.stopCheckSession();
                    break;
                default:
                    break;
            }
        };
        this.issuer = "https://account.trumpf.com";
        this.clientId = "";
        this.clientName = "";
        this.scope = "oicd";
        this.redirectUri = "";
        this.checkSessionUri = "";
        this.failedTimeout = 3;
        this.keepAliveTimeout = 600;
        this.debug = undefined;
        this.loggedIn = undefined;
        this.sessionState = undefined;
        this.checkSessionActive = false;
    }
    handleLoggedIn() {
        if (!this.loggedIn) {
            this.stopCheckSession();
            this.sessionState = undefined;
        }
        if (this.loggedIn && this.checkSessionEnabled) {
            this.startCheckSession();
        }
    }
    /** Retrieves the current token data */
    async getTokenData() {
        return this.authTokenData;
    }
    componentWillLoad() {
        this.authClientId = this.clientId;
        this.domElement.removeAttribute("client-id");
        this.authClient = this.clientName.length > 0 ? getAuthClient(this.clientName) : getAuthClient();
        this.uid = getRandomString(32);
        if (!this.redirectUri) {
            if (TRUMPF_ISSUERS_PROD.includes(this.issuer)) {
                this.redirectUri = TRUMPF_CALLBACK_URI_PROD;
            }
            else if (TRUMPF_ISSUERS_STAGE.includes(this.issuer)) {
                this.redirectUri = TRUMPF_CALLBACK_URI_STAGE;
            }
        }
        if (!this.checkSessionUri) {
            if (TRUMPF_ISSUERS_PROD.includes(this.issuer)) {
                this.checkSessionUri = TRUMPF_CHECK_SESSION_URI_PROD;
            }
            else if (TRUMPF_ISSUERS_STAGE.includes(this.issuer)) {
                this.checkSessionUri = TRUMPF_CHECK_SESSION_URI_STAGE;
            }
        }
        this.checkSessionEnabled = !!this.checkSessionUri || window.origin === getUrlOrigin(this.redirectUri);
    }
    componentDidLoad() {
        this.initSilentAuthDebounced();
    }
    disconnectedCallback() {
        this.stopCheckSession();
    }
    render() {
        let checkSessionSrc = "";
        let checkSessionSrcDoc = "";
        if (this.loggedIn && this.checkSessionEnabled && this.sessionState) {
            // OICD checksession iframe src
            const opIframeSrc = `${this.issuer}/sessiontracker?${getQueryString({
                client_id: this.authClientId,
                redirect_uri: this.redirectUri
            })}`;
            if (window.origin === getUrlOrigin(this.redirectUri)) {
                // Same origin: render check-session iframe internally
                checkSessionSrcDoc = getCheckSessionIframeSrcdoc({
                    session_state: this.sessionState,
                    client_id: this.authClientId,
                    op_src: opIframeSrc
                });
            }
            else if (this.checkSessionUri) {
                // Different origin: load check-session iframe from checkSessionUri
                checkSessionSrc = `${this.checkSessionUri}?${getQueryString({
                    session_state: this.sessionState,
                    client_id: this.authClientId,
                    op_src: opIframeSrc,
                    origin: window.origin
                })}`;
            }
        }
        const activeSessionSrc = `${this.issuer}/activesession?${getQueryString({
            client_id: this.authClientId
        })}`;
        return (h(Host, { key: '31e9f14161625e4281b6b99f9a85df9b62dbc920', class: "uxc-silent-auth" }, h("iframe", { key: '0997926978507de8d5bb2eae8a4833e7bbb22056', id: `silent-auth-${this.uid}`, ref: (el) => (this.authIframe = el) }), h("iframe", { key: 'a15b70707504d266aac9d1fcd20fd9e4325ed099', id: `silent-auth-session-${this.uid}`, src: activeSessionSrc, ref: (el) => (this.activeSessionIframe = el) }), this.checkSessionEnabled && this.checkSessionActive && this.loggedIn && (h(Fragment, { key: '4c5384907daefd0e91d2ee91ea61009279d27caf' }, checkSessionSrcDoc && h("iframe", { key: 'da83698df74470081d4cd33d87eff74bce08e3c3', id: `silent-auth-check-session-${this.uid}`, srcDoc: checkSessionSrcDoc }), !checkSessionSrcDoc && checkSessionSrc && (h("iframe", { key: 'c3a2cec83a88cdb1e527cd7c0f8108f8d2482cd6', id: `silent-auth-check-session-${this.uid}`, src: checkSessionSrc }))))));
    }
    static get is() { return "uxc-silent-auth"; }
    static get originalStyleUrls() {
        return {
            "$": ["silent-auth.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["silent-auth.css"]
        };
    }
    static get properties() {
        return {
            "issuer": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "URL of the identity provider"
                },
                "attribute": "issuer",
                "reflect": false,
                "defaultValue": "\"https://account.trumpf.com\""
            },
            "clientId": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Auth client id (is hidden when the component mounts)"
                },
                "attribute": "client-id",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "clientName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Name of the client, used to get the scoped AuthClient instance"
                },
                "attribute": "client-name",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "scope": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Requested scopes"
                },
                "attribute": "scope",
                "reflect": false,
                "defaultValue": "\"oicd\""
            },
            "redirectUri": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Auth callback URL. Only needs to be provided for issuers other than https://identity*.trumpf.com"
                },
                "attribute": "redirect-uri",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "checkSessionUri": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Optional check-session iframe URL. The origin must match the redirectUri."
                },
                "attribute": "check-session-uri",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "failedTimeout": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Time in seconds after which the authentication flow is considered as failed"
                },
                "attribute": "failed-timeout",
                "reflect": false,
                "defaultValue": "3"
            },
            "keepAliveTimeout": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Time in seconds after which the authentication flow is rerun to keep the session alive"
                },
                "attribute": "keep-alive-timeout",
                "reflect": false,
                "defaultValue": "600"
            },
            "debug": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If set to true, infos are logged to the console"
                },
                "attribute": "debug",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "loggedIn": {},
            "sessionState": {},
            "checkSessionActive": {}
        };
    }
    static get events() {
        return [{
                "method": "uxAuth",
                "name": "uxAuth",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted after the authentication flow was completed.\nAt this point either an `access_token` was fetched successfully or silent authentication failed for some reason."
                },
                "complexType": {
                    "original": "{\n        success: boolean;\n        client_name?: string;\n        error?: string | null;\n        access_token?: string;\n        expires_in?: number;\n        id_token?: string;\n        refresh_token?: string;\n        token_type?: string;\n    }",
                    "resolved": "{ success: boolean; client_name?: string; error?: string; access_token?: string; expires_in?: number; id_token?: string; refresh_token?: string; token_type?: string; }",
                    "references": {}
                }
            }, {
                "method": "uxLogout",
                "name": "uxLogout",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the session was invalidated after successful authentication."
                },
                "complexType": {
                    "original": "{\n        client_name?: string;\n    }",
                    "resolved": "{ client_name?: string; }",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "getTokenData": {
                "complexType": {
                    "signature": "() => Promise<AuthTokenData | undefined>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "AuthTokenData": {
                            "location": "import",
                            "path": "@trumpf/ux.utils",
                            "id": "../../node_modules/@trumpf/ux.utils/dist/types/index.d.ts::AuthTokenData"
                        }
                    },
                    "return": "Promise<AuthTokenData>"
                },
                "docs": {
                    "text": "Retrieves the current token data",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "domElement"; }
    static get watchers() {
        return [{
                "propName": "loggedIn",
                "methodName": "handleLoggedIn"
            }];
    }
}
//# sourceMappingURL=silent-auth.js.map
